import { PlusCircleFilled, SearchOutlined } from '@ant-design/icons';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type IProductNames } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getFilterOption, getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  productAssignRelatedProducts,
  productsFindInElasticSearch,
} from '../../reducers/product/reducer';
import { productsLoadAllShort } from '../../reducers/products/reducer';
import { SPACING } from '../../theme/tokens';
import ExternalLink from '../external-link';

interface IFormMutiselect {
  productsUUIDs: string[];
}

interface IFormElasticSearch {
  query: string;
}

const { Title } = Typography;

function Related(): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productNames = useSelector((s: RootState) => s.Product.names);
  const productRelatedUUIDs = useSelector(
    (s: RootState) => s.Product.relatedUUIDs,
  );
  const productRelatedFromElasticSearch = useSelector(
    (s: RootState) => s.Product.relatedProductsFromElasticSearch,
  );
  const productsShort = useSelector((s: RootState) => s.Products.productsShort);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [formMultiselect] = Form.useForm<IFormMutiselect>();
  const [formElasticSearch] = Form.useForm<IFormElasticSearch>();

  const [contentElement, setContentElement] = useState(<Skeleton active />);

  const onFinishMultiselect = useCallback(
    ({ productsUUIDs }: { productsUUIDs: string[] }): void => {
      dispatch(productAssignRelatedProducts(productUUID, productsUUIDs));
    },
    [dispatch, productUUID],
  );

  const onFinishElasticSearch = useCallback(
    ({ query }: { query: string }): void => {
      if (!languageType) {
        return;
      }

      dispatch(productsFindInElasticSearch(languageType.code, query));
    },
    [dispatch, languageType?.code],
  );

  useEffect((): void => {
    const productName: string | null = getTranslation(
      productNames,
      languageType,
    );

    const queryUE: string = productName || '';

    const actualQuery: string = formElasticSearch.getFieldValue('query');

    if (!actualQuery) {
      formElasticSearch.setFieldsValue({
        query: queryUE,
      });
    }
  }, [productNames, languageType?.code, formElasticSearch]);

  useEffect((): void => {
    if (!productsShort) {
      setContentElement(<Skeleton active />);
      return;
    }

    const products: IProductNames[] =
      productsShort.filter(
        ({ uuid }: IProductNames): boolean => uuid !== productUUID,
      ) || [];

    let relatedProductsFromElasticSearchEl: React.ReactElement | undefined;

    if (productRelatedFromElasticSearch) {
      const actualProductUUIDs: string[] = [
        ...[productUUID],
        ...productRelatedUUIDs,
        ...(formMultiselect.getFieldValue('productsUUIDs') || []),
      ];

      const relatedProductsFromElasticFiltered: string[] =
        productRelatedFromElasticSearch?.filter(
          (uuid: string): boolean => !actualProductUUIDs.includes(uuid),
        ) || [];

      relatedProductsFromElasticSearchEl = (
        <div>
          {relatedProductsFromElasticFiltered.map(
            (pUUID: string): React.ReactElement | undefined => {
              const product: IProductNames | undefined = products.find(
                (p: IProductNames): boolean => p.uuid === pUUID,
              );

              if (!product) {
                return undefined;
              }
              const productName: string | null = getTranslation(
                product.names,
                languageType,
              );

              const linkName = `${productName || '****'}`;

              return (
                <div key={pUUID}>
                  <Space size={SPACING.lg} align="center">
                    <Tooltip title="Add related product">
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<PlusCircleFilled />}
                        onClick={(): void => {
                          const productsUUIDs = new Set<string>(
                            formMultiselect.getFieldValue('productsUUIDs'),
                          );

                          productsUUIDs.add(pUUID);

                          formMultiselect.setFieldsValue({
                            productsUUIDs: [...productsUUIDs],
                          });
                        }}
                        disabled={productInProcess}
                      />
                    </Tooltip>

                    <ExternalLink
                      to={`../product/${pUUID}`}
                      label={linkName}
                      ariaLabel="Open product in new tab"
                    />
                  </Space>
                </div>
              );
            },
          )}
        </div>
      );
    } else {
      relatedProductsFromElasticSearchEl = <Skeleton active />;
    }

    const contentUE: React.ReactElement = (
      <Row gutter={SPACING.lg}>
        <Col xs={24} sm={12}>
          <Form
            form={formMultiselect}
            onFinish={onFinishMultiselect}
            layout="vertical"
            initialValues={{
              productsUUIDs: productRelatedUUIDs,
            }}
          >
            <Row gutter={SPACING.lg}>
              <Col span={18}>
                <Form.Item name="productsUUIDs">
                  <Select
                    mode="multiple"
                    allowClear
                    size="large"
                    placeholder="Select related products"
                    disabled={productInProcess}
                    showSearch={{
                      autoClearSearchValue: false,
                      filterOption: getFilterOption,
                      filterSort: getFilterSort,
                    }}
                    options={products.map((product: IProductNames) => {
                      const productName: string | null = getTranslation(
                        product.names,
                        languageType,
                      );

                      const optionName = `${productName || '****'} → ${
                        product.uuid
                      }`;

                      return { value: product.uuid, label: optionName };
                    })}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  disabled={productInProcess}
                >
                  Add related products
                </Button>
              </Col>
            </Row>
          </Form>

          <div>
            {productRelatedUUIDs.map(
              (relatedProductUUID: string): React.ReactElement | undefined => {
                const product: IProductNames | undefined = products.find(
                  (p: IProductNames): boolean => p.uuid === relatedProductUUID,
                );

                if (!product) {
                  return undefined;
                }

                const productName: string | null = getTranslation(
                  product.names,
                  languageType,
                );

                const linkName = `${productName || '****'}`;

                return (
                  <div key={relatedProductUUID}>
                    <ExternalLink
                      to={`../product/${relatedProductUUID}`}
                      label={linkName}
                      ariaLabel="Open product in new tab"
                    />
                  </div>
                );
              },
            )}
          </div>
        </Col>

        <Col xs={24} sm={12}>
          <Form form={formElasticSearch} onFinish={onFinishElasticSearch}>
            <Row gutter={SPACING.lg}>
              <Col span={20}>
                <Form.Item
                  name="query"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Start writing to search products by name or description"
                  />
                </Form.Item>
              </Col>

              <Col span={4}>
                <Button
                  size="large"
                  color="primary"
                  htmlType="submit"
                  type="primary"
                  disabled={productInProcess}
                >
                  <SearchOutlined />
                </Button>
              </Col>
            </Row>

            {relatedProductsFromElasticSearchEl}
          </Form>
        </Col>
      </Row>
    );

    setContentElement(contentUE);
  }, [
    formElasticSearch,
    formMultiselect,
    languageType?.code,
    onFinishElasticSearch,
    onFinishMultiselect,
    productsShort,
    productUpdatedAt,
  ]);

  useEffect((): void => {
    dispatch(productsLoadAllShort());
  }, [dispatch]);

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        Related ({productRelatedUUIDs.length})
      </Title>

      {contentElement}
    </>
  );
}

export default React.memo(Related);
